import * as React from "react";
import IFloatingPanel from "src/models/interfaces/IFloatingPanel";

/**
 * Tiempo en segundos.
 */
const deleteMessageTime: number = 2;

const ErrorListContext = React.createContext<IFloatingPanel[]>([]);
const ErrorSetterContext = React.createContext<(error: IFloatingPanel) => void>(() => { });
const ErrorDeleterContext = React.createContext<(errorId: number) => void>(() => { });

export const ErrorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [errors, setErrors] = React.useState<IFloatingPanel[]>([]);
  
  /**
   * @function deleteError
   * @description Elimina un error a la lista de errores.
   * @param {number} errorId id del error a eliminar.
   */
  function deleteError(errorId: number): void {
    setErrors((prev) => prev.filter((e) => e.id !== errorId));
  }

  /**
   * @function StateProvider.addError
   * @description Agrega un error a la lista de errores.
   * @param {IFloatingPanel} err Error a agregar.
   */
  const addError = React.useCallback((err: IFloatingPanel) => {
    const newError: IFloatingPanel = { ...err, id: Date.now(), dateTime: err.dateTime ?? new Date() };
    
    setErrors((prev) => {
      const updated = [...prev, newError];
      return updated;
    });

    if (newError.id) {
      setTimeout(deleteError, deleteMessageTime * 60 * 1000, newError.id);
    }
  }, []);

  return (
    <ErrorListContext.Provider value={errors}>
      <ErrorSetterContext.Provider value={addError}>
        <ErrorDeleterContext.Provider value={deleteError}>
          {children}
        </ErrorDeleterContext.Provider>
      </ErrorSetterContext.Provider>
    </ErrorListContext.Provider>
  ); 
};

export function useErrorList(): IFloatingPanel[] {
  return React.useContext(ErrorListContext);
}

export function useErrorSetter(): (error: IFloatingPanel) => void {
  return React.useContext(ErrorSetterContext);
}

export function useErrorDeleter(): (errorId: number) => void {
  return React.useContext(ErrorDeleterContext);
}
